import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api';

/**
 * Engagement Pulse API using RTK Query
 *
 * AUTHENTICATED API - Token required
 * Provides endpoints for:
 * - Configuration management
 * - Conversation CRUD
 * - Dashboard metrics and aggregations
 *
 * Follows 2025 RTK Query best practices:
 * - Centralized server state management
 * - Automatic caching and request deduplication
 * - Optimistic updates with cache invalidation
 * - LIST/ITEM tagging pattern for granular updates
 */
export const engagementPulseApi = createApi({
  reducerPath: 'engagementPulseApi',
  baseQuery,
  tagTypes: ['EngagementConfig', 'EngagementConversations', 'EngagementMetrics', 'EngagementDashboard'],
  // Cache configuration for dashboard data
  keepUnusedDataFor: 300, // 5 minutes cache
  refetchOnMountOrArgChange: 60, // Refetch if data is 1 minute old
  endpoints: (builder) => ({
    // ==================== CONFIGURATION ====================
    /**
     * Get engagement pulse configuration for the account
     * @returns Configuration object
     */
    getConfig: builder.query({
      query: () => '/api/v1/engagement-pulse/config/',
      providesTags: ['EngagementConfig'],
    }),

    /**
     * Update engagement pulse configuration
     * @param {Object} updates - Configuration fields to update
     * @returns Updated configuration object
     */
    updateConfig: builder.mutation({
      query: (updates) => ({
        url: '/api/v1/engagement-pulse/config/update/',
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['EngagementConfig'],
    }),

    // ==================== CONVERSATIONS ====================
    /**
     * Get list of engagement pulse conversations
     * @param {Object} params - Query parameters
     * @param {string} [params.week_start_date] - Filter by week (YYYY-MM-DD)
     * @param {string} [params.status] - Filter by status (PENDING, IN_PROGRESS, COMPLETED, EXPIRED, SKIPPED)
     * @param {string} [params.employee_id] - Filter by employee UUID
     * @returns Array of conversation objects
     */
    getConversations: builder.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.week_start_date) searchParams.append('week_start_date', params.week_start_date);
        if (params.status) searchParams.append('status', params.status);
        if (params.employee_id) searchParams.append('employee_id', params.employee_id);
        const queryString = searchParams.toString();
        return `/api/v1/engagement-pulse/conversations/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'EngagementConversations', id })),
              { type: 'EngagementConversations', id: 'LIST' },
            ]
          : [{ type: 'EngagementConversations', id: 'LIST' }],
    }),

    /**
     * Get single conversation with responses
     * @param {string} conversationId - Conversation UUID
     * @returns Conversation object with responses array
     */
    getConversation: builder.query({
      query: (conversationId) => `/api/v1/engagement-pulse/conversations/${conversationId}/`,
      providesTags: (result, error, conversationId) => [
        { type: 'EngagementConversations', id: conversationId },
      ],
    }),

    /**
     * Create new engagement pulse conversation
     * @param {Object} data
     * @param {string} data.employee - Employee UUID
     * @param {string} data.week_start_date - Week start date (YYYY-MM-DD)
     * @returns Created conversation object
     */
    createConversation: builder.mutation({
      query: (data) => ({
        url: '/api/v1/engagement-pulse/conversations/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'EngagementConversations', id: 'LIST' }],
    }),

    /**
     * Add response to conversation
     * @param {Object} params
     * @param {string} params.conversationId - Conversation UUID
     * @param {string} params.response_type - 'RATING' or 'QUALITATIVE'
     * @param {string} [params.rating_value] - For RATING: 'FANTASTIC', 'GOOD', 'NORMAL', 'POOR', 'TERRIBLE'
     * @param {string} [params.text_content] - For QUALITATIVE: feedback text
     * @returns Created response object
     */
    addResponse: builder.mutation({
      query: ({ conversationId, ...data }) => ({
        url: `/api/v1/engagement-pulse/conversations/${conversationId}/responses/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { conversationId }) => [
        { type: 'EngagementConversations', id: conversationId },
      ],
    }),

    /**
     * Complete conversation and trigger metric calculation
     * @param {string} conversationId - Conversation UUID
     * @returns Updated conversation object with COMPLETED status
     */
    completeConversation: builder.mutation({
      query: (conversationId) => ({
        url: `/api/v1/engagement-pulse/conversations/${conversationId}/complete/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, conversationId) => [
        { type: 'EngagementConversations', id: conversationId },
        { type: 'EngagementConversations', id: 'LIST' },
        { type: 'EngagementMetrics', id: 'LIST' },
        'EngagementDashboard',
      ],
    }),

    // ==================== METRICS ====================
    /**
     * Get list of engagement metrics
     * @param {Object} params - Query parameters
     * @param {string} [params.week_start_date] - Filter by week (YYYY-MM-DD)
     * @param {string} [params.employee_id] - Filter by employee UUID
     * @param {number} [params.min_score] - Filter by minimum score
     * @param {number} [params.max_score] - Filter by maximum score
     * @returns Array of metric objects
     */
    getMetrics: builder.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.week_start_date) searchParams.append('week_start_date', params.week_start_date);
        if (params.employee_id) searchParams.append('employee_id', params.employee_id);
        if (params.min_score !== undefined) searchParams.append('min_score', params.min_score);
        if (params.max_score !== undefined) searchParams.append('max_score', params.max_score);
        const queryString = searchParams.toString();
        return `/api/v1/engagement-pulse/metrics/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'EngagementMetrics', id })),
              { type: 'EngagementMetrics', id: 'LIST' },
            ]
          : [{ type: 'EngagementMetrics', id: 'LIST' }],
    }),

    // ==================== DASHBOARD ====================
    /**
     * Get dashboard summary metrics
     * @param {string} [week_start_date] - Week start date (YYYY-MM-DD), defaults to current week
     * @returns Summary object with engagement stats
     */
    getDashboardSummary: builder.query({
      query: ({ week_start_date, metric_type } = {}) => {
        const searchParams = new URLSearchParams();
        if (week_start_date) searchParams.append('week_start_date', week_start_date);
        if (metric_type) searchParams.append('metric_type', metric_type);
        const queryString = searchParams.toString();
        return `/api/v1/engagement-pulse/dashboard/summary/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: (_result, _error, arg) => [
        { type: 'EngagementDashboard', id: `SUMMARY-${arg?.metric_type || 'engagement'}` },
      ],
    }),

    /**
     * Get engagement timeseries data
     * @param {Object} params - Query parameters
     * @param {number} [params.weeks=12] - Number of weeks to include
     * @param {string} [params.entity_type] - 'department', 'manager', or 'employee'
     * @param {string} [params.entity_id] - Entity identifier for filtering
     * @param {string} [params.end_week] - End week (YYYY-MM-DD), defaults to current week
     * @returns Array of {week_start_date, avg_engagement, response_count}
     */
    getTimeseries: builder.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.weeks) searchParams.append('weeks', params.weeks);
        if (params.entity_type) searchParams.append('entity_type', params.entity_type);
        if (params.entity_id) searchParams.append('entity_id', params.entity_id);
        if (params.end_week) searchParams.append('end_week', params.end_week);
        if (params.metric_type) searchParams.append('metric_type', params.metric_type);
        const queryString = searchParams.toString();
        return `/api/v1/engagement-pulse/metrics/timeseries/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: (_result, _error, params) => [
        { type: 'EngagementDashboard', id: `TIMESERIES-${params?.metric_type || 'engagement'}` },
      ],
    }),

    /**
     * Get engagement heatmap data
     * @param {Object} params - Query parameters
     * @param {number} [params.weeks=12] - Number of weeks to include
     * @param {string} [params.group_by='department'] - 'department', 'manager', or 'employee'
     * @param {string} [params.end_week] - End week (YYYY-MM-DD), defaults to current week
     * @param {string} [params.department] - Filter by department (for drill-down)
     * @param {string} [params.manager] - Filter by manager (for drill-down)
     * @returns Array of {entity_type, entity_id, entity_name, weekly_scores}
     */
    getHeatmap: builder.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.weeks) searchParams.append('weeks', params.weeks);
        if (params.group_by) searchParams.append('group_by', params.group_by);
        if (params.end_week) searchParams.append('end_week', params.end_week);
        if (params.department) searchParams.append('department', params.department);
        if (params.manager) searchParams.append('manager', params.manager);
        if (params.metric_type) searchParams.append('metric_type', params.metric_type);
        const queryString = searchParams.toString();
        return `/api/v1/engagement-pulse/metrics/heatmap/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: (_result, _error, params) => [
        { type: 'EngagementDashboard', id: `HEATMAP-${params?.metric_type || 'engagement'}` },
      ],
    }),

    /**
     * Get detailed metrics with conversations for modal display
     * @param {Object} params - Query parameters
     * @param {string} params.week_start_date - Week start date (YYYY-MM-DD)
     * @param {string} params.group_by - 'department', 'manager', or 'employee'
     * @param {string} params.entity_id - Entity identifier
     * @returns Array of metric objects with conversation details
     */
    getMetricDetail: builder.query({
      query: (params) => {
        const searchParams = new URLSearchParams();
        searchParams.append('week_start_date', params.week_start_date);
        searchParams.append('group_by', params.group_by);
        searchParams.append('entity_id', params.entity_id);
        return `/api/v1/engagement-pulse/metrics/detail/?${searchParams.toString()}`;
      },
      providesTags: [{ type: 'EngagementMetrics', id: 'DETAIL' }],
    }),

    /**
     * Get list of weeks that have engagement data
     * @returns Array of week_start_date strings (YYYY-MM-DD), ordered most recent first
     */
    getAvailableWeeks: builder.query({
      query: () => '/api/v1/engagement-pulse/metrics/available-weeks/',
      providesTags: [{ type: 'EngagementDashboard', id: 'AVAILABLE_WEEKS' }],
    }),

    // ==================== OPERATIONS ====================
    /**
     * Send engagement pulse WhatsApp templates to eligible employees
     * @param {Object} [body] - Optional request body
     * @param {string[]} [body.employee_ids] - Optional list of employee UUIDs to send to
     * @returns {Object} { sent, skipped, failed }
     */
    sendPulses: builder.mutation({
      query: (body) => ({
        url: '/api/v1/engagement-pulse/operations/send-pulses/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'EngagementConversations', id: 'LIST' },
        'EngagementDashboard',
      ],
    }),

    /**
     * Bulk process metrics for completed conversations
     * @returns {Object} { processed, errors }
     */
    processMetrics: builder.mutation({
      query: () => ({
        url: '/api/v1/engagement-pulse/operations/process-metrics/',
        method: 'POST',
      }),
      invalidatesTags: [
        { type: 'EngagementMetrics', id: 'LIST' },
        'EngagementDashboard',
      ],
    }),
  }),
});

export const {
  // Configuration
  useGetConfigQuery,
  useUpdateConfigMutation,
  // Conversations
  useGetConversationsQuery,
  useGetConversationQuery,
  useCreateConversationMutation,
  useAddResponseMutation,
  useCompleteConversationMutation,
  // Metrics
  useGetMetricsQuery,
  useGetMetricDetailQuery,
  // Dashboard
  useGetDashboardSummaryQuery,
  useGetTimeseriesQuery,
  useGetHeatmapQuery,
  useGetAvailableWeeksQuery,
  // Operations
  useSendPulsesMutation,
  useProcessMetricsMutation,
} = engagementPulseApi;
